export type ActivitySample = { timestampMs: number; messagesPerSecond: number | null }
type ActivityInterval = { from: number; to: number }

export type ChatActivity = ActivityInterval & {
  version: 1
  sampleIntervalMs: 60000
  unit: 'messages/second'
  samples: ActivitySample[]
  lastSuccessfulCollectionAt: number | null
  latestSampleAt: number | null
  stale: boolean
  coverage: { available: ActivityInterval[]; gaps: ActivityInterval[] }
}

export function resolveActivityApiUrl(value: string | undefined, origin: string): string | null {
  if (!value?.trim()) return null
  const raw = value.trim()
  const url = raw.startsWith('/') ? new URL(raw, origin) : new URL(raw)
  if (url.protocol !== 'https:' && url.protocol !== 'http:') throw new Error('Use an HTTP(S) chat activity API URL')
  url.hash = ''
  return url.toString()
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function isTimestamp(value: unknown): value is number {
  return typeof value === 'number' && Number.isSafeInteger(value) && value >= 0
}

function invalidData(): never {
  throw new Error('Chat activity service returned invalid data')
}

/** Validate the versioned Worker contract, including the exact requested window. */
export function parseChatActivity(payload: unknown, window: ActivityInterval): ChatActivity {
  if (!isRecord(payload) || payload.version !== 1 || payload.from !== window.from || payload.to !== window.to
    || payload.sampleIntervalMs !== 60000 || payload.unit !== 'messages/second' || typeof payload.stale !== 'boolean'
    || !Array.isArray(payload.samples) || !isRecord(payload.coverage)
    || (payload.lastSuccessfulCollectionAt !== null && !isTimestamp(payload.lastSuccessfulCollectionAt))
    || (payload.latestSampleAt !== null && !isTimestamp(payload.latestSampleAt))) invalidData()

  let previous = -1
  const samples: ActivitySample[] = payload.samples.map((sample: unknown) => {
    if (!isRecord(sample) || !isTimestamp(sample.timestampMs) || sample.timestampMs % 60000 !== 0
      || sample.timestampMs < window.from || sample.timestampMs >= window.to || sample.timestampMs <= previous
      || (sample.messagesPerSecond !== null && (typeof sample.messagesPerSecond !== 'number'
        || !Number.isFinite(sample.messagesPerSecond) || sample.messagesPerSecond < 0))) invalidData()
    previous = sample.timestampMs
    return { timestampMs: sample.timestampMs, messagesPerSecond: sample.messagesPerSecond }
  })

  const parseIntervals = (value: unknown): ActivityInterval[] => {
    if (!Array.isArray(value)) invalidData()
    let end = window.from
    return value.map((interval: unknown) => {
      if (!isRecord(interval) || !isTimestamp(interval.from) || !isTimestamp(interval.to)
        || interval.from < end || interval.to <= interval.from || interval.to > window.to) invalidData()
      end = interval.to
      return { from: interval.from, to: interval.to }
    })
  }
  const coverage = { available: parseIntervals(payload.coverage.available), gaps: parseIntervals(payload.coverage.gaps) }
  let cursor = window.from
  for (const interval of [...coverage.available, ...coverage.gaps].sort((a, b) => a.from - b.from)) {
    if (interval.from !== cursor) invalidData()
    cursor = interval.to
  }
  if (cursor !== window.to) invalidData()
  return {
    version: 1, ...window, sampleIntervalMs: 60000, unit: 'messages/second', samples,
    lastSuccessfulCollectionAt: payload.lastSuccessfulCollectionAt,
    latestSampleAt: payload.latestSampleAt,
    stale: payload.stale,
    coverage,
  }
}

export async function fetchChatActivity(
  endpoint: string,
  window: ActivityInterval,
  signal: AbortSignal,
  refresh = false,
): Promise<ChatActivity> {
  const url = new URL(endpoint)
  url.searchParams.set('from', String(window.from))
  url.searchParams.set('to', String(window.to))
  let response: Response
  try {
    response = await fetch(url, {
      signal: AbortSignal.any([signal, AbortSignal.timeout(15000)]),
      headers: { Accept: 'application/json' },
      cache: refresh ? 'no-cache' : 'default',
    })
  } catch (error) {
    if (signal.aborted) throw error
    if (error instanceof Error && error.name === 'TimeoutError') throw new Error('Chat activity request timed out')
    throw new Error('Could not reach the chat activity service')
  }
  if (!response.ok) throw new Error(`Chat activity is unavailable (HTTP ${response.status})`)
  let payload: unknown
  try { payload = await response.json() } catch { invalidData() }
  return parseChatActivity(payload, window)
}
